/**
 * Automatically crops an image to the specified aspect ratio by centering the crop.
 * This function loads the image, calculates center crop coordinates, and returns
 * the cropped image as a Blob without requiring user interaction.
 *
 * @param file - The image file to crop
 * @param targetAspectRatio - Desired aspect ratio (e.g., 3/4 for portrait, 16/9 for landscape)
 * @returns Promise<Blob> - The cropped image as a Blob
 */
export async function autoCropImage(file: File, targetAspectRatio: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Canvas context not available"));
      return;
    }

    // Create object URL for the file
    const imageUrl = URL.createObjectURL(file);

    image.onload = () => {
      // Clean up object URL
      URL.revokeObjectURL(imageUrl);

      const sourceWidth = image.width;
      const sourceHeight = image.height;
      const sourceAspectRatio = sourceWidth / sourceHeight;

      let cropWidth: number;
      let cropHeight: number;
      let cropX: number;
      let cropY: number;

      // Calculate crop dimensions to match target aspect ratio
      if (sourceAspectRatio > targetAspectRatio) {
        // Source is wider than target - crop width
        cropHeight = sourceHeight;
        cropWidth = sourceHeight * targetAspectRatio;
        cropX = (sourceWidth - cropWidth) / 2;
        cropY = 0;
      } else {
        // Source is taller than target - crop height
        cropWidth = sourceWidth;
        cropHeight = sourceWidth / targetAspectRatio;
        cropX = 0;
        cropY = (sourceHeight - cropHeight) / 2;
      }

      // Set canvas size to cropped dimensions
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      // Draw the cropped portion of the image
      ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Canvas toBlob failed"));
          }
        },
        "image/jpeg",
        0.95 // High quality for try-on photos
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error("Failed to load image for auto-cropping"));
    };

    image.src = imageUrl;
  });
}
